<template>
	<div class="fd-member-name-ea">
		<div class="fd-member-party-fr">
			<div class="fd-member-name">
				<template v-if="suggested">
					<object-link :src-id="p.id" :src-type="SrcType.PARTY" class="mlink">{{ partyFileAs }}</object-link>
					-
					<member-badges :membership="m"></member-badges>
					<tag-input v-if="editable" v-model="m.role" ref="roleInput" :placeholder="env.msg('member.role.placeholder')" style="display:inline-block" :min-width="80"></tag-input>
					<span v-else-if="m.role" class="fd-role">{{ m.role }}</span>
				</template>
				<template v-else>
					<suggester-part :data="p" :part="memberSuggesterPart" @suggested="newPartySuggested"></suggester-part>
				</template>
			</div>
			<slot></slot>
		</div>
		<ea-list v-if="canViewContactInfo" :list="p.eaddresses"></ea-list>
	</div>
</template>

<script setup lang="ts">
import {SrcType} from "@t/meta/SourceType";
import MemberBadges from "@fd/editors/member/MemberBadges.vue";
import TagInput from "@fd/editors/suggester/TagInput.vue";
import ObjectLink from "@fd/helpers/ObjectLink.vue";
import type {ActiveMembership, CustomerMember, PartySimple} from "@t/customer/CustomerMember";
import {computed, ref, nextTick, inject} from "vue";
import {injectEnv} from "@fd/Environment";
import SuggesterPart from "@fd/editors/suggester/SuggesterPart.vue";
import type {Suggester, SuggesterBase} from "@t/fd/editor/Suggester";
import EaList from "@fd/editors/member/EaList.vue";
import {injectMaskingLib} from "@fd/Masking.ts";
import {AccessMaskingTest, MaskingTest, OrMaskingTest, RoleMaskingTest} from "@t/fd/layout/Masked.ts";
import {grantAccess, hasAccess, secureRootKey} from "@t/security/ObjectAccess.ts";
import {obscureText} from "@/utils/FormatUtil.ts";

const props = defineProps<{
	member: CustomerMember,
	/** membership role is editable */
	editable: boolean,
	customizeSuggester?: Partial<SuggesterBase>
}>();
const emit = defineEmits(["update:role"]);
const roleInput = ref(null);

const p = computed<PartySimple>(() => props.member.party);
const m = computed<ActiveMembership>(() => props.member.membership);
const suggested = computed<boolean>(() => !!p.value.id);
const env = injectEnv();
const mlib = injectMaskingLib();

const role = computed<string>({
	get():string {
		return m.value.role;
	},
	set(v:string):void {
		if (props.editable) emit('update:role', v);
	}
});

const canViewContactInfo = computed<boolean>(() => {
	const theTest = <OrMaskingTest>{
		on: "or",
		tests: [
			<RoleMaskingTest>{ on: "role", name:"party.contacts" },
			<AccessMaskingTest>{ on: "access", edit: true }
		]
	};
	const result = mlib.evaluateTest(props.member, false, theTest);
	return result;
});

const canView = computed<boolean>(() => {
	return hasAccess(props.member, "view");
});

const partyFileAs = computed<string>(() => {
	return canView.value ? p.value.fileAs : obscureText(p.value.fileAs);
});


/** Dynamically created part object for suggesting party */
const memberSuggesterPart = computed(() => {
	const customized = props.customizeSuggester ? props.customizeSuggester : {};
	let sg: Suggester = {
		type: "suggest",
		property: "id",
		url: "suggestfeed.do",
		suggester: "MemberSuggester",
		minTags: 0,
		maxTags: 1,
		pickerLabel: env.msg('placeholder.party.suggester'),
		propagate: [
			{d: "id", s: "id.main", c: "integer", empty: 0},
			{d: "fileAs", s: "title.main", empty: ""},
			{d: "icon", s: "icon", ifmissing: "party"}
		],
		tag: [
			{d: "id", t: "id", empty: 0},
			{d: "fileAs", t: "title", empty: 0, optional: true},
			{d: "fileAs", t: "name", empty: ""}
		],
		...customized
	};
	return sg;
});

function newPartySuggested() {
	grantAccess(props.member, "view");	// newly suggested member will always be viewable (even if the user may not have view permission)
	nextTick(() => {
		if (roleInput.value) {
			roleInput.value.focus();
		}
	});
}

</script>

<style>
.fd-member-party-fr {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	box-sizing: border-box;
}

/* Block name-row | eaddresses vertically */
.fd-member-name-ea {
	user-select: text;
	-webkit-user-select: text;
}

/* Static (non-editable) role span */
.fd-role {
	padding-left: 2px;
}
.mlink {
	font-weight: 500;
	--link-fg: var(--fg);
	--link-decoration: none;
}
</style>
